import { z } from 'zod';

export const PLACE_CUSTOMER_CLASSIFICATIONS = [
  'RETAIL',
  'WHOLESALE',
  'RESELLER',
  'DISTRIBUTOR',
  'INDUSTRY',
  'GOVERNMENT',
  'NON_PROFIT',
  'KEY_ACCOUNT',
] as const;

export const PlaceCustomerClassificationSchema = z.enum(PLACE_CUSTOMER_CLASSIFICATIONS);

export type PlaceCustomerClassification = z.infer<typeof PlaceCustomerClassificationSchema>;

export const PLACE_CUSTOMER_ACQUISITION_SOURCES = [
  'REFERRAL',
  'SOCIAL_MEDIA',
  'SEARCH_ENGINE',
  'PAID_ADS',
  'WALK_IN',
  'OUTDOOR',
  'EVENT',
  'EMAIL_MARKETING',
  'OUTBOUND',
  'OTHER',
] as const;

export const PlaceCustomerAcquisitionSourceSchema = z.enum(PLACE_CUSTOMER_ACQUISITION_SOURCES);

export type PlaceCustomerAcquisitionSource = z.infer<typeof PlaceCustomerAcquisitionSourceSchema>;

export const PLACE_CUSTOMER_ACQUISITION_SUBCATEGORIES_BY_SOURCE = {
  SOCIAL_MEDIA: ['INSTAGRAM', 'FACEBOOK', 'TIKTOK', 'YOUTUBE', 'WHATSAPP'],
  PAID_ADS: ['RADIO', 'TV', 'NEWSPAPER'],
  SEARCH_ENGINE: ['GOOGLE', 'BING'],
  REFERRAL: ['CUSTOMER', 'EMPLOYEE', 'SUPPLIER'],
  OUTDOOR: ['BILLBOARD', 'BUS', 'FLYER'],
  EVENT: ['TRADE_SHOW', 'WORKSHOP_EVENT'],
  EMAIL_MARKETING: ['NEWSLETTER', 'CAMPAIGN'],
  OUTBOUND: ['PHONE', 'WHATSAPP', 'VISIT'],
  WALK_IN: [],
  OTHER: [],
} as const satisfies Record<PlaceCustomerAcquisitionSource, readonly string[]>;

export const PLACE_CUSTOMER_ACQUISITION_SUBCATEGORIES = [
  'INSTAGRAM',
  'FACEBOOK',
  'TIKTOK',
  'YOUTUBE',
  'WHATSAPP',
  'RADIO',
  'TV',
  'NEWSPAPER',
  'GOOGLE',
  'BING',
  'CUSTOMER',
  'EMPLOYEE',
  'SUPPLIER',
  'BILLBOARD',
  'BUS',
  'FLYER',
  'TRADE_SHOW',
  'WORKSHOP_EVENT',
  'NEWSLETTER',
  'CAMPAIGN',
  'PHONE',
  'VISIT',
] as const;

export const PlaceCustomerAcquisitionSubcategorySchema = z.enum(
  PLACE_CUSTOMER_ACQUISITION_SUBCATEGORIES,
);

export type PlaceCustomerAcquisitionSubcategory = z.infer<
  typeof PlaceCustomerAcquisitionSubcategorySchema
>;

export function acquisitionSubcategoriesForSource(
  source: PlaceCustomerAcquisitionSource | null,
): readonly PlaceCustomerAcquisitionSubcategory[] {
  if (source === null) {
    return [];
  }

  return PLACE_CUSTOMER_ACQUISITION_SUBCATEGORIES_BY_SOURCE[source];
}

export function acquisitionSourceRequiresSubcategory(
  source: PlaceCustomerAcquisitionSource | null,
): boolean {
  if (source === null) {
    return false;
  }

  return acquisitionSubcategoriesForSource(source).length > 0;
}

export const MoneyCentsStringSchema = z.string().regex(/^\d+$/);

export type MoneyCentsString = z.infer<typeof MoneyCentsStringSchema>;

export const PlaceCustomerResponseSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  customerRoleId: z.uuid(),
  referredByCustomerRoleId: z.uuid().nullable(),
  classification: PlaceCustomerClassificationSchema.nullable(),
  acquisitionSource: PlaceCustomerAcquisitionSourceSchema.nullable(),
  acquisitionSubcategory: PlaceCustomerAcquisitionSubcategorySchema.nullable(),
  customerSince: z.iso.datetime(),
  lastInteraction: z.iso.datetime().nullable(),
  creditLimitCents: MoneyCentsStringSchema,
  creditUsedCents: MoneyCentsStringSchema,
  totalRevenueCents: MoneyCentsStringSchema,
  totalCostCents: MoneyCentsStringSchema,
  active: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PlaceCustomerResponse = z.infer<typeof PlaceCustomerResponseSchema>;

export const LinkPlaceCustomerBodySchema = z.object({
  customerRoleId: z.uuid(),
  creditLimitCents: MoneyCentsStringSchema.optional(),
  classification: PlaceCustomerClassificationSchema.optional(),
  acquisitionSource: PlaceCustomerAcquisitionSourceSchema.optional(),
  acquisitionSubcategory: PlaceCustomerAcquisitionSubcategorySchema.nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  referredByCustomerRoleId: z.uuid().nullable().optional(),
});

export type LinkPlaceCustomerBody = z.infer<typeof LinkPlaceCustomerBodySchema>;

export const LinkPlaceCustomerResponseSchema = PlaceCustomerResponseSchema;

export type LinkPlaceCustomerResponse = z.infer<typeof LinkPlaceCustomerResponseSchema>;

export const UpdatePlaceCustomerBodySchema = z.object({
  classification: PlaceCustomerClassificationSchema.nullable().optional(),
  acquisitionSource: PlaceCustomerAcquisitionSourceSchema.nullable().optional(),
  acquisitionSubcategory: PlaceCustomerAcquisitionSubcategorySchema.nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  active: z.boolean().optional(),
  referredByCustomerRoleId: z.uuid().nullable().optional(),
  lastInteraction: z.iso.datetime().nullable().optional(),
});

export type UpdatePlaceCustomerBody = z.infer<typeof UpdatePlaceCustomerBodySchema>;

export const UpdatePlaceCustomerCreditBodySchema = z.object({
  creditLimitCents: MoneyCentsStringSchema,
});

export type UpdatePlaceCustomerCreditBody = z.infer<typeof UpdatePlaceCustomerCreditBodySchema>;

export const UpsertTenantCustomerCreditBodySchema = z.object({
  creditLimitCents: MoneyCentsStringSchema,
});

export type UpsertTenantCustomerCreditBody = z.infer<typeof UpsertTenantCustomerCreditBodySchema>;

export const UpdatePlaceCustomerResponseSchema = z.object({
  placeCustomerId: z.uuid(),
  changedFields: z.array(z.string()),
});

export type UpdatePlaceCustomerResponse = z.infer<typeof UpdatePlaceCustomerResponseSchema>;

export const UpdatePlaceCustomerCreditResponseSchema = z.object({
  placeCustomerId: z.uuid(),
  creditLimitCents: MoneyCentsStringSchema,
});

export type UpdatePlaceCustomerCreditResponse = z.infer<
  typeof UpdatePlaceCustomerCreditResponseSchema
>;

export const UnlinkPlaceCustomerResponseSchema = z.object({
  placeCustomerId: z.uuid(),
});

export type UnlinkPlaceCustomerResponse = z.infer<typeof UnlinkPlaceCustomerResponseSchema>;

export const ListPlaceCustomersResponseSchema = z.object({
  items: z.array(PlaceCustomerResponseSchema),
});

export type ListPlaceCustomersResponse = z.infer<typeof ListPlaceCustomersResponseSchema>;

export const GetPlaceCustomerByIdResponseSchema = z.object({
  placeCustomer: PlaceCustomerResponseSchema,
});

export type GetPlaceCustomerByIdResponse = z.infer<typeof GetPlaceCustomerByIdResponseSchema>;

export const ResolveCustomerCreditQuerySchema = z.object({
  placeId: z.uuid(),
});

export type ResolveCustomerCreditQuery = z.infer<typeof ResolveCustomerCreditQuerySchema>;

export const ResolveCustomerCreditResponseSchema = z.object({
  availableCents: MoneyCentsStringSchema,
  placeRemainingCents: MoneyCentsStringSchema,
  tenantRemainingCents: MoneyCentsStringSchema.nullable(),
});

export type ResolveCustomerCreditResponse = z.infer<typeof ResolveCustomerCreditResponseSchema>;

export const UpsertTenantCustomerCreditResponseSchema = z.object({
  customerRoleId: z.uuid(),
  creditLimitCents: MoneyCentsStringSchema,
});

export type UpsertTenantCustomerCreditResponse = z.infer<
  typeof UpsertTenantCustomerCreditResponseSchema
>;

export const PlaceIdParamsSchema = z.object({
  placeId: z.uuid(),
});

export type PlaceIdParams = z.infer<typeof PlaceIdParamsSchema>;

export const PlaceCustomerParamsSchema = z.object({
  placeId: z.uuid(),
  placeCustomerId: z.uuid(),
});

export type PlaceCustomerParams = z.infer<typeof PlaceCustomerParamsSchema>;
